import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ecommerceService } from '../../services/ecommerceService';

// Async thunks
export const fetchWishlists = createAsyncThunk(
  'ecommerceWishlist/fetchWishlists',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getWishlists();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchWishlist = createAsyncThunk(
  'ecommerceWishlist/fetchWishlist',
  async (wishlistId, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getWishlist(wishlistId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createWishlist = createAsyncThunk(
  'ecommerceWishlist/createWishlist',
  async (data, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.createWishlist(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteWishlist = createAsyncThunk(
  'ecommerceWishlist/deleteWishlist',
  async (wishlistId, { rejectWithValue }) => {
    try {
      await ecommerceService.deleteWishlist(wishlistId);
      return wishlistId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addItemToWishlist = createAsyncThunk(
  'ecommerceWishlist/addItemToWishlist',
  async ({ wishlistId, data }, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.addToWishlist(wishlistId, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removeItemFromWishlist = createAsyncThunk(
  'ecommerceWishlist/removeItemFromWishlist',
  async ({ wishlistId, itemId }, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.removeFromWishlist(wishlistId, itemId);
      return { wishlistId, itemId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const moveItemToCart = createAsyncThunk(
  'ecommerceWishlist/moveItemToCart',
  async ({ wishlistId, itemId, quantity = 1 }, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.moveToCart(wishlistId, itemId, quantity);
      return { wishlistId, itemId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const quickAddToWishlist = createAsyncThunk(
  'ecommerceWishlist/quickAddToWishlist',
  async ({ webProductId, variantId = null }, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.quickAddToWishlist(webProductId, variantId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const quickRemoveFromWishlist = createAsyncThunk(
  'ecommerceWishlist/quickRemoveFromWishlist',
  async (webProductId, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.quickRemoveFromWishlist(webProductId);
      return { webProductId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const checkInWishlist = createAsyncThunk(
  'ecommerceWishlist/checkInWishlist',
  async (webProductId, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.checkInWishlist(webProductId);
      return { webProductId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  wishlists: [],
  currentWishlist: null,
  wishlistItems: {},  // Map of productId -> boolean
  loading: false,
  itemLoading: {},  // Map of productId -> loading state
  error: null,
};

const ecommerceWishlistSlice = createSlice({
  name: 'ecommerceWishlist',
  initialState,
  reducers: {
    clearWishlistError: (state) => {
      state.error = null;
    },
    setWishlistItemStatus: (state, action) => {
      const { productId, inWishlist } = action.payload;
      state.wishlistItems[productId] = inWishlist;
    },
    clearCurrentWishlist: (state) => {
      state.currentWishlist = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Wishlists
      .addCase(fetchWishlists.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWishlists.fulfilled, (state, action) => {
        state.loading = false;
        state.wishlists = action.payload.results || action.payload;
      })
      .addCase(fetchWishlists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Single Wishlist
      .addCase(fetchWishlist.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.currentWishlist = action.payload;
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Wishlist
      .addCase(createWishlist.fulfilled, (state, action) => {
        state.wishlists.push(action.payload);
      })

      // Delete Wishlist
      .addCase(deleteWishlist.fulfilled, (state, action) => {
        state.wishlists = state.wishlists.filter(w => w.id !== action.payload);
      })

      // Add Item to Wishlist
      .addCase(addItemToWishlist.fulfilled, (state, action) => {
        state.currentWishlist = action.payload;
      })

      // Remove Item from Wishlist
      .addCase(removeItemFromWishlist.fulfilled, (state, action) => {
        if (state.currentWishlist) {
          state.currentWishlist.items = state.currentWishlist.items.filter(
            item => item.id !== action.payload.itemId
          );
        }
      })

      // Move to Cart
      .addCase(moveItemToCart.fulfilled, (state, action) => {
        if (state.currentWishlist) {
          state.currentWishlist.items = state.currentWishlist.items.filter(
            item => item.id !== action.payload.itemId
          );
        }
      })

      // Quick Add
      .addCase(quickAddToWishlist.pending, (state, action) => {
        const productId = action.meta.arg.webProductId;
        state.itemLoading[productId] = true;
      })
      .addCase(quickAddToWishlist.fulfilled, (state, action) => {
        const productId = action.meta.arg.webProductId;
        state.itemLoading[productId] = false;
        state.wishlistItems[productId] = true;
      })
      .addCase(quickAddToWishlist.rejected, (state, action) => {
        const productId = action.meta.arg.webProductId;
        state.itemLoading[productId] = false;
      })

      // Quick Remove
      .addCase(quickRemoveFromWishlist.pending, (state, action) => {
        state.itemLoading[action.meta.arg] = true;
      })
      .addCase(quickRemoveFromWishlist.fulfilled, (state, action) => {
        state.itemLoading[action.payload.webProductId] = false;
        state.wishlistItems[action.payload.webProductId] = false;
      })
      .addCase(quickRemoveFromWishlist.rejected, (state, action) => {
        state.itemLoading[action.meta.arg] = false;
      })

      // Check In Wishlist
      .addCase(checkInWishlist.fulfilled, (state, action) => {
        state.wishlistItems[action.payload.webProductId] = action.payload.data.in_wishlist;
      });
  },
});

export const {
  clearWishlistError,
  setWishlistItemStatus,
  clearCurrentWishlist,
} = ecommerceWishlistSlice.actions;

export default ecommerceWishlistSlice.reducer;
